// Angular
import {APP_INITIALIZER, ErrorHandler} from '@angular/core';
import {catchError, firstValueFrom, map, of, tap} from 'rxjs';
import {provideHttpClient, withInterceptors} from '@angular/common/http';
import {provideAnimations} from '@angular/platform-browser/animations';
import {provideServiceWorker} from '@angular/service-worker';

// Third-party libraries
import {CookieService} from 'ngx-cookie-service';
import {provideEnvironmentNgxMask} from 'ngx-mask';
import {provideToastConfig, ToastService} from '@sms-ir/ui-components';

// App config
import {ngxMaskConfig} from '@app/core/config/ngx-mask.config';

// App environment
import {environment} from '@env/environment';

// App API
import {ApiExecutor, ApiValidationService} from '@app/core/api';

// App interceptors
import {apiResponseInterceptor, authInterceptor, loadingInterceptor} from '@app/core/interceptors';

// App services
import {GlobalErrorHandler} from './services/global-error.handler';
import {LayoutService} from './services/layout.service';
import {TokenRefreshService} from './services/base';
import {PrefixStoreService} from '@app/core/store/prefix-store.service';
import {storeProviders} from '@app/core/store/store.providers';
import {AppApiService} from './services/app-api.service';
import {ServerTimeService} from '@shared/services/server-time.service';

/**
 * Core providers for the application.
 * Replaces the former AppModule provider block.
 */
export const coreProviders = [
  provideEnvironmentNgxMask(ngxMaskConfig),
  provideHttpClient(withInterceptors([
    authInterceptor,
    loadingInterceptor,
    apiResponseInterceptor,
  ])),
  provideAnimations(),
  provideServiceWorker('ngsw-worker.js', {
    enabled: environment.production,
  }),
  provideToastConfig({duration: 4000, position: 'top-right', maxStack: 5}),
  {provide: ErrorHandler, useClass: GlobalErrorHandler},
  ToastService,
  CookieService,
  {
    provide: APP_INITIALIZER,
    useFactory: (ts: ServerTimeService) => () => ts.initSync(),
    deps: [ServerTimeService],
    multi: true,
  },
  ...storeProviders,
  {
    provide: APP_INITIALIZER,
    useFactory: (appApi: AppApiService, prefixStore: PrefixStoreService) => () =>
      firstValueFrom(
        appApi.getPrefix().pipe(
          tap(response => {
            if (response.success && response.result) {
              prefixStore.setPrefixes(response.result);
            }
          }),
          catchError(err => {
            console.error('[StoreInit] failed to load prefix data', err);
            return of(void 0);
          }),
          map(() => void 0),
        ),
      ),
    deps: [AppApiService, PrefixStoreService],
    multi: true,
  },
  ApiExecutor,
  ApiValidationService,
  TokenRefreshService,
  LayoutService,
];
